import React, { memo, useCallback, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import PropTypes from 'prop-types';

import useSort from 'hooks/useSort';

import { expandedRowContentStyles } from '../Conditions.styles';
import { groupTasksByEntity } from '../Conditions.utils';

import ConditionTasksGroup from './components/ConditionTasksGroup';
import TaskInfoRow from './components/TaskInfoRow';
import { getTasksTableConfig } from './ConditionTasksTable.config';
import { taskTableDefaultOrder } from './ConditionTasksTable.constants';

const ConditionTasksTable = ({
  tasks,
  conditionId,
  taskTypesMap = {},
  entityIndex = {},
  isBrokerCompany = false,
  canRemoveTasks = false,
}) => {
  const { order, onRequestSort } = useSort(taskTableDefaultOrder);
  const [expandedTaskId, setExpandedTaskId] = useState(null);

  const onToggleTaskExpand = useCallback(taskId => {
    setExpandedTaskId(prev => (prev === taskId ? null : taskId));
  }, []);

  const onCollapseTask = useCallback(() => setExpandedTaskId(null), []);

  const tableConfig = useMemo(
    () => getTasksTableConfig({ conditionId, canRemoveTasks, expandedTaskId, onToggleTaskExpand }),
    [conditionId, canRemoveTasks, expandedTaskId, onToggleTaskExpand],
  );

  const groups = useMemo(
    () => groupTasksByEntity({ tasks, taskTypesMap, entityIndex, isBrokerCompany }),
    [tasks, taskTypesMap, entityIndex, isBrokerCompany],
  );

  const expandedTask = useMemo(
    () => tasks.find(task => task.taskId === expandedTaskId),
    [tasks, expandedTaskId],
  );

  const expandedTaskContent = useMemo(
    () =>
      expandedTask ? (
        <TaskInfoRow conditionId={conditionId} task={expandedTask} onClose={onCollapseTask} />
      ) : null,
    [expandedTask, conditionId, onCollapseTask],
  );

  // A lone ungrouped bucket (broker view / no entity data) renders exactly like the flat
  // table did — no heading. Any real entity grouping shows a heading per group.
  const withHeading = !(groups.length === 1 && groups[0].isUngrouped);

  return (
    <Box sx={expandedRowContentStyles}>
      {groups.map(group => (
        <ConditionTasksGroup
          config={tableConfig}
          expandedTaskContent={expandedTaskContent}
          expandedTaskId={expandedTaskId}
          group={group}
          key={group.key}
          order={order}
          withHeading={withHeading}
          onRequestSort={onRequestSort}
        />
      ))}
    </Box>
  );
};

ConditionTasksTable.propTypes = {
  tasks: PropTypes.arrayOf(PropTypes.object).isRequired,
  conditionId: PropTypes.string.isRequired,
  taskTypesMap: PropTypes.object,
  entityIndex: PropTypes.object,
  isBrokerCompany: PropTypes.bool,
  canRemoveTasks: PropTypes.bool,
};

export default memo(ConditionTasksTable);
